/**
 * Cursive capital letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cursiveCapitalLettersPack: ToilePack = {
    slug: 'cursive-capital-letters',
    category: 'illustration',
    name: 'Cursive capital letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'chart', label: 'Capital chart', subject: 'a chart of the 26 cursive capitals "A" to "Z" on ruled handwriting lines in six rows, small numbered arrows showing where each stroke starts'},
        {key: 'trace', label: 'Trace sheet', subject: 'a tracing sheet with the cursive capitals "A" to "H", each written once in solid line and twice in dotted line on ruled rows'},
        {key: 'flourish', label: 'Flourished', subject: 'a chart of the cursive capitals "A" to "Z" with looped Spencerian-style ovals and long entry strokes, five rows on a clean page'},
    ],
    styles: [
        {key: 'school', label: 'School form', style: 'plain school cursive capitals with a round, even stroke on ruled lines with a dashed midline', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'arrows', label: 'Stroke arrows', style: 'cursive capitals each with small numbered arrows and a dot marking where the stroke starts', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
        {key: 'signing', label: 'Signing hand', style: 'tall elegant cursive capitals with a light, quick stroke and long entry strokes', palette: 'black ink only', framing: 'centered on a white sheet with a generous margin'},
    ],
    directions: ['a name', 'small letters', 'dotted trace'],
    directionHint: 'try: a name',
    seo: {
        title: 'Cursive Capital Letters: Chart, Trace Sheet, Flourishes',
        metaDescription: 'Nine cursive capital sheets: A to Z with stroke arrows for school, a trace sheet and flourished capitals for signing cards.',
        h1: 'Cursive capital letters: a stroke chart, a trace sheet, flourished capitals',
        tagline: 'Cursive capitals in three guidance levels, from school forms to signing hands.',
        intro: 'Cursive capitals are the letters most people forget how to form. The nine sheets give a chart of the 26 capitals with numbered stroke arrows, a trace sheet for A to H, and a flourished set for signing cards. Each comes as a plain school form, with stroke arrows, or as a tall signing hand. Ask for a name, small letters or dotted trace.',
        useCases: [
            'a practice sheet for a child learning capitals',
            'a reference chart for an adult relearning them',
            'a signing hand for cards',
            'a trace sheet for a classroom',
        ],
        faq: [
            {q: 'How many letters are on the chart?', a: 'The stroke chart shows all 26 capitals, A to Z, in six rows.'},
            {q: 'What do the arrows mean?', a: 'Each stroke has a small numbered arrow and a dot where it starts, so the order and direction of the strokes can be seen.'},
            {q: 'Is the trace sheet complete?', a: 'It covers A to H only. Each letter is written once in solid line and twice in dotted line.'},
            {q: 'Can I get my name in these capitals?', a: 'Yes. Ask for a name and the capitals are set to it.'},
        ],
    },
}
